import { CommuneFields } from './CommuneFields';
import type { DossierFields as F } from '@clim/domain';
import { useState } from 'react';
import { useEngine } from '../lib/session';
import { Field, Input, Select, Textarea } from './ui';

export type DossierDraft = Partial<F>;

/**
 * Champs du dossier (CDC §4) partagés création / modification :
 * type, précisions, géolocalisation ponctuelle, adresse d'intervention, conditions d'installation.
 */
export function DossierFieldsEditor({
  value,
  onChange,
  errors = {},
  lockType = false,
}: {
  value: DossierDraft;
  onChange(v: DossierDraft): void;
  errors?: Record<string, string>;
  lockType?: boolean;
}) {
  const engine = useEngine();
  const types = engine
    .list('dossier_type')
    .filter((t) => t.isActive || t.id === value.dossierTypeId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const walls = engine
    .list('reason')
    .filter((r) => r.category === 'wall_type' && r.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const type = types.find((t) => t.id === value.dossierTypeId);
  const [siteOpen, setSiteOpen] = useState(!!value.siteAddressLine1);
  const [geoMsg, setGeoMsg] = useState<string | null>(null);
  const set = (patch: DossierDraft) => onChange({ ...value, ...patch });

  const captureGeo = () => {
    // Consentement explicite (CDC §20) + autorisation du navigateur ; capture unique, aucun suivi.
    if (!confirm('Enregistrer votre position actuelle sur ce dossier ? (capture unique, pas de suivi)'))
      return;
    if (!navigator.geolocation) return setGeoMsg('Géolocalisation indisponible sur cet appareil');
    setGeoMsg('Localisation…');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        set({
          geoLat: Number(p.coords.latitude.toFixed(6)),
          geoLng: Number(p.coords.longitude.toFixed(6)),
          geoAccuracyM: Math.round(p.coords.accuracy),
          geoCapturedAt: new Date(p.timestamp).toISOString(),
        });
        setGeoMsg(null);
      },
      () => setGeoMsg('Position refusée ou indisponible'),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  };

  return (
    <>
      {!lockType && (
        <div className="segmented wrap" role="radiogroup" aria-label="Type de dossier">
          {types.map((t) => (
            <button
              type="button"
              key={t.id}
              className={value.dossierTypeId === t.id ? 'on' : ''}
              onClick={() => set({ dossierTypeId: t.id })}
              aria-pressed={value.dossierTypeId === t.id}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <Field label="Intitulé (facultatif)" error={errors.title}>
        <Input
          value={value.title ?? ''}
          onChange={(e) => set({ title: e.target.value })}
          placeholder={type?.label ?? 'Ex. : 2 splits salon + chambre'}
        />
      </Field>
      <Field label="Précisions / besoin" error={errors.description}>
        <Textarea value={value.description ?? ''} onChange={(e) => set({ description: e.target.value })} />
      </Field>

      <div className="geo">
        <button type="button" className="btn" onClick={captureGeo}>
          📍 Je suis chez le client
        </button>
        {value.geoLat != null && (
          <span className="muted">
            Position enregistrée ({value.geoLat}, {value.geoLng}, ±{value.geoAccuracyM ?? '?'} m){' '}
            <button
              type="button"
              className="link"
              onClick={() => set({ geoLat: null, geoLng: null, geoAccuracyM: null, geoCapturedAt: null })}
            >
              effacer
            </button>
          </span>
        )}
        {geoMsg && <span className="muted">{geoMsg}</span>}
      </div>

      <label className="check">
        <input type="checkbox" checked={siteOpen} onChange={(e) => setSiteOpen(e.target.checked)} />
        Adresse d’intervention différente de l’adresse de facturation
      </label>
      {siteOpen && (
        <>
          <Field label="Adresse d’intervention" error={errors.siteAddressLine1}>
            <Input
              value={value.siteAddressLine1 ?? ''}
              onChange={(e) => set({ siteAddressLine1: e.target.value })}
            />
          </Field>
          <div className="grid2">
            <CommuneFields
              postalCode={value.sitePostalCode}
              city={value.siteCity}
              errors={{ postalCode: errors.sitePostalCode, city: errors.siteCity }}
              labels={{ postalCode: 'Code postal du chantier', city: 'Commune du chantier' }}
              onChange={(v) => set({ sitePostalCode: v.postalCode, siteCity: v.city })}
            />
          </div>
        </>
      )}
      {!siteOpen && errors.siteAddressLine1 && <p className="field-error">{errors.siteAddressLine1}</p>}

      {/* à chaque dossier : en hauteur ou non (le lieu change d'un dossier à l'autre) ; support pour une installation */}
      <fieldset>
        <legend>
          {type?.settings?.requiresInstallConditions
            ? 'Conditions d’installation'
            : 'Conditions d’intervention'}
        </legend>
        {Boolean(type?.settings?.requiresInstallConditions) && (
          <Field label="Type de mur / support" error={errors.installWallTypeId}>
            <Select
              value={value.installWallTypeId ?? ''}
              onChange={(e) => set({ installWallTypeId: e.target.value || null })}
            >
              <option value="">Choisir…</option>
              {walls.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className={`field${errors.installAtHeight ? ' has-error' : ''}`}>
          <span className="field-label" id="at-height-label">
            Intervention en hauteur ?
          </span>
          <div className="segmented" role="radiogroup" aria-labelledby="at-height-label">
            {[
              [false, 'Non'],
              [true, 'Oui'],
            ].map(([v, l]) => (
              <button
                type="button"
                role="radio"
                aria-checked={value.installAtHeight === v}
                key={String(v)}
                className={value.installAtHeight === v ? 'on' : ''}
                onClick={() =>
                  set({
                    installAtHeight: v as boolean,
                    ...(v ? {} : { installAccess: 'none', installHeightM: null }),
                  })
                }
              >
                {l as string}
              </button>
            ))}
          </div>
          {errors.installAtHeight && <span className="field-error">{errors.installAtHeight}</span>}
        </div>
        {value.installAtHeight && (
          <div className="grid2">
            <Field label="Accès" error={errors.installAccess}>
              <Select
                value={value.installAccess ?? ''}
                onChange={(e) => set({ installAccess: (e.target.value || null) as F['installAccess'] })}
              >
                <option value="">Choisir…</option>
                <option value="ladder">Échelle</option>
                <option value="nacelle">Nacelle</option>
              </Select>
            </Field>
            <Field label="Hauteur approximative (m)" error={errors.installHeightM}>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={value.installHeightM ?? ''}
                onChange={(e) =>
                  set({ installHeightM: e.target.value === '' ? null : Number(e.target.value) })
                }
              />
            </Field>
          </div>
        )}
      </fieldset>
    </>
  );
}
